// Every word of the "most asked" rows both Astra chats open with (#5258;
// founder pick A of the #5243 mocks, 2026-10-23:
// https://claude.ai/artifact/YaB5xeY3qvLRf3JHhDXJTC, committed as
// docs/specs/astra-popular-questions/mocks.html). The words are the design
// spec's (section 4).
//
// The server counts and ranks (counselor_agent/astra_popular.py); the page
// never computes a rank. It shows ONE live-checked seed per ranked topic from
// astraChatSeeds.json, so a rank moves only the order, never the words. No
// numbers on the page (founder answer 2: the counts stay on the Desk), no
// price, plan or credit words.
import SEEDS from './astraChatSeeds.json';

export const POPULAR = {
    // Astra Lite (signed out)
    liteLabel: 'Most asked this week',
    insight: (first, second) => (second
        ? `Most asked this week: ${first}, then ${second}.`
        : `Most asked this week: ${first}.`),
    // signed-in Astra
    studentLabel: 'Students are asking',
    // under the shelf while it reads the visitors' week (signed-in counts
    // under their own floor, founder answer 4)
    studentBasis: 'Asked most on Stratia this week.',
};

// Connect's "Prompts for your agent" (the Playbook folded in, founder answer 1).
export const CONNECT_PROMPTS = {
    title: 'Prompts for your agent',
    sub: 'Pick one and it opens in your agent, already written. Ask Astra runs it here.',
    categoriesLabel: 'Prompt categories',
};

// The closed topic names (astra_popular.SEEDED_TOPICS plus the two with no
// seed yet, pinned by a test). 'other', 'distress' and 'news' have no name:
// they are never shown.
export const TOPIC_NAMES = {
    school_odds: 'chances at a school',
    test_policy: 'testing',
    school_facts: "a school's facts",
    major_choice: 'majors',
    cost_aid: 'cost and aid',
    aid_process: 'how aid works',
    activities: 'activities',
    essays: 'essays',
    deadlines: 'deadlines',
    list_building: 'building a list',
    timeline: 'timeline',
    summer_programs: 'summer programs',
};

const ALL_SEEDS = [...(SEEDS.popular || []), ...SEEDS.tabs.flatMap((t) => t.seeds)];

/** The one seed the row shows for a topic: the popular list's first, then
 *  the tabs' in their order. null for a topic with no checked seed. */
export function seedForTopic(topic) {
    return ALL_SEEDS.find((s) => s.topic === topic) || null;
}

const ranked = (popular, bases) => (popular && bases.includes(popular.basis) && Array.isArray(popular.topics)
    ? popular.topics.filter((t) => typeof t === 'string' && TOPIC_NAMES[t]).slice(0, 3) : []);

/** Astra Lite's row: [{seed, topic, rank}] in the server's order; [] on the
 *  fallback, a missing block or a topic with no seed. */
export function liteChips(popular) {
    return ranked(popular, ['week'])
        .map((topic) => ({ topic, seed: seedForTopic(topic) }))
        .filter((c) => c.seed)
        .map((c, i) => ({ ...c, rank: i + 1 }));
}

/** The line under the row, words only, or null. */
export function insightLine(popular) {
    const topics = ranked(popular, ['week']);
    if (!topics.length) return null;
    return POPULAR.insight(TOPIC_NAMES[topics[0]], topics[1] ? TOPIC_NAMES[topics[1]] : null);
}

/** A college page sheet's own seeds in the week's order (no label, no line):
 *  ranked topics first, the rest as they were. */
export function rankSeeds(seeds, popular) {
    const order = ranked(popular, ['week']);
    if (!order.length || !Array.isArray(seeds)) return seeds;
    const at = (s) => { const i = order.indexOf(s.topic); return i < 0 ? order.length : i; };
    return seeds.map((s, i) => ({ s, i })).sort((a, b) => at(a.s) - at(b.s) || a.i - b.i).map((x) => x.s);
}

// Signed in, in the student's voice (design spec section 4): a question names
// a school from her own list where it can, the first school (the facts chip
// the second, so the shelf does not name one school three times); with an
// empty list it falls back to words that name none, or to the live-checked
// seed. Never a school she has not listed.
const STUDENT_WORDS = {
    school_odds: ([first]) => (first ? `Where do I stand at ${first}?`
        : "How hard is it to get into a school I'm looking at?"),
    test_policy: ([first]) => (first ? 'Which of my schools are test-blind?' : null),
    school_facts: ([first, second]) => ((second || first) ? `What's ${second || first}'s acceptance rate?` : null),
    essays: ([first]) => (first ? `What do ${first}'s essays ask?` : null),
    cost_aid: ([first]) => (first ? `What does ${first} cost after aid?` : null),
    deadlines: ([first]) => (first ? `When are ${first}'s deadlines?` : null),
    list_building: ([first]) => (first ? 'Is my list balanced?' : null),
    major_choice: ([first]) => (first ? `Is my major direct entry at ${first}?` : 'How do I pick a major?'),
    timeline: () => 'What should I be working on now?',
};

/** Signed-in Astra's shelf: {chips: [{topic, label, rank}], basisLine}, or
 *  null on the fallback (the opening screen stays as it is). */
export function studentShelf(popular) {
    const topics = ranked(popular, ['students', 'week']);
    if (!topics.length) return null;
    const schools = (Array.isArray(popular.schools) ? popular.schools : [])
        .filter((s) => typeof s === 'string' && s.trim()).slice(0, 2);
    const chips = topics.map((topic) => {
        const own = STUDENT_WORDS[topic] ? STUDENT_WORDS[topic](schools) : null;
        return { topic, label: own || seedForTopic(topic)?.q || null };
    }).filter((c) => c.label).map((c, i) => ({ ...c, rank: i + 1 }));
    if (!chips.length) return null;
    return { chips, basisLine: popular.basis === 'week' ? POPULAR.studentBasis : null };
}
